import React, { useEffect, useMemo, useState } from "react";
import { Dialog, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import baseUrl from "../../config/baseUrl";
import { maskToken } from "./AuthorizeDialog";
import { showSuccess } from "../../utils/toast";

// Administration > API Reference > Try It Out: a small request runner for a
// single endpoint. It sends the request exactly as edited here to the real
// backend (baseUrl). Protected endpoints automatically get the page-wide token
// set via "Authorize" (apiToken); public endpoints are sent without one.
// Fields come only from the backend code (api.params, generated from the
// handler's validation rules and the request fields it reads) and from the
// route's {path} placeholders - nothing is invented.

const METHOD_STYLES = {
  GET: "bg-emerald-50 text-emerald-700 border-emerald-200",
  POST: "bg-blue-50 text-blue-700 border-blue-200",
  PUT: "bg-violet-50 text-violet-700 border-violet-200",
  PATCH: "bg-amber-50 text-amber-700 border-amber-200",
  DELETE: "bg-red-50 text-red-700 border-red-200",
};

const BODY_METHODS = ["POST", "PUT", "PATCH"];
const needsToken = (api) => /Bearer|sanctum/i.test(api.auth || "");
const isFileParam = (rules) => /(^|\|)(file|image|mimes:|mimetypes:)/i.test(rules || "");

const isRequired = (rules) => (rules || "").split("|").includes("required");

// Readable hint from the validation rules (type/format only).
const ruleHint = (p) => {
  if (p.from === "path") return "part of the URL path";
  if (p.from === "code") return "read by the handler (no validation rule)";
  const parts = (p.rules || "").split("|").filter((r) => r && !["required", "nullable", "sometimes", "bail"].includes(r));
  return parts.join(", ");
};

// Route placeholders such as {document_id} are always required.
const pathParamsOf = (endpoint) => [...endpoint.matchAll(/\{([^}?]+)\??\}/g)].map((m) => m[1]);

// Request fields that can be sent as text (file uploads can't go in JSON/query).
const fieldsOf = (api) => {
  const fields = (api.params || []).filter((p) => !isFileParam(p.rules));
  // required first, keeping the backend's order otherwise
  return [...fields.filter((p) => isRequired(p.rules)), ...fields.filter((p) => !isRequired(p.rules))];
};

// Empty template - the user only fills in the values.
const buildBody = (fields) => {
  const body = {};
  fields.forEach((p) => {
    body[p.name] = /(^|\|)array(\||$)/.test(p.rules || "") ? [] : "";
  });
  return JSON.stringify(body, null, 2);
};

const isEmptyValue = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
const toText = (v) => (typeof v === "object" ? JSON.stringify(v) : String(v));

// URL from the values object: {path} placeholders are filled from their keys;
// for GET/DELETE every other non-empty key becomes a query parameter
// (arrays as name[]=a&name[]=b, the form Laravel reads as an array).
const buildUrl = (endpoint, values = {}, withQuery = false) => {
  const pathNames = new Set(pathParamsOf(endpoint));
  const path = endpoint.replace(/\{([^}?]+)\??\}/g, (whole, name) =>
    isEmptyValue(values[name]) ? whole : encodeURIComponent(toText(values[name])),
  );
  const qs = withQuery
    ? Object.entries(values)
        .filter(([k, v]) => !pathNames.has(k) && !isEmptyValue(v))
        .flatMap(([k, v]) =>
          Array.isArray(v)
            ? v.map((item) => `${encodeURIComponent(`${k}[]`)}=${encodeURIComponent(toText(item))}`)
            : [`${encodeURIComponent(k)}=${encodeURIComponent(toText(v))}`],
        )
        .join("&")
    : "";
  return `${baseUrl}${path}${qs ? `?${qs}` : ""}`;
};

// Drop optional fields left as "" so they stay optional on the server.
const stripEmptyOptional = (text, fields) => {
  try {
    const obj = JSON.parse(text);
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return text;
    const optional = new Set(fields.filter((p) => !isRequired(p.rules)).map((p) => p.name));
    Object.keys(obj).forEach((k) => {
      if (optional.has(k) && obj[k] === "") delete obj[k];
    });
    return JSON.stringify(obj, null, 2);
  } catch (e) {
    return text;
  }
};

const ReqBadge = ({ required }) =>
  required ? (
    <span className="rounded border border-red-200 bg-red-50 px-1 text-[9px] font-semibold uppercase text-red-600">Required</span>
  ) : (
    <span className="rounded border border-gray-200 bg-gray-50 px-1 text-[9px] font-semibold uppercase text-gray-500">Optional</span>
  );

// One form field: label (name, * and Required/Optional, path/query) over its
// input. The rule hint is shown on hover.
const ParamField = ({ name, required, hint, where, value, onChange, placeholder }) => (
  <label className="flex min-w-0 flex-col gap-1">
    <span className="flex min-w-0 items-center gap-1.5">
      <Tooltip title={hint || ""} arrow disableHoverListener={!hint}>
        <code className="truncate font-mono text-[11px] font-semibold text-gray-800">
          {name}
          {required && <span className="font-sans font-bold text-red-600"> *</span>}
        </code>
      </Tooltip>
      <ReqBadge required={required} />
      {where && <span className="ml-auto shrink-0 text-[10px] text-gray-400">{where}</span>}
    </span>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || (required ? `Enter ${name}` : "Optional")}
      spellCheck={false}
      className={`h-8 w-full rounded-md border px-2 font-mono text-xs text-gray-800 focus:border-nbl-primary focus:outline-none ${
        required && !value ? "border-red-200 bg-red-50/40" : "border-gray-300"
      }`}
    />
  </label>
);

// Shared section wrapper so every method's dialog has the same structure.
const Section = ({ title, extra, children, footer }) => (
  <section>
    <Label extra={extra}>{title}</Label>
    {children}
    {footer}
  </section>
);

const Hint = ({ tone = "gray", children }) => (
  <div className={`mt-1 text-[11px] ${tone === "amber" ? "text-amber-700" : tone === "red" ? "text-red-700" : "text-gray-500"}`}>
    {children}
  </div>
);

const statusClass = (status) => {
  if (!status) return "bg-red-50 text-red-700 border-red-200";
  if (status < 300) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (status < 400) return "bg-sky-50 text-sky-700 border-sky-200";
  if (status < 500) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-red-50 text-red-700 border-red-200";
};

const Label = ({ children, extra }) => (
  <div className="mb-1 flex items-center justify-between gap-2">
    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{children}</span>
    {extra}
  </div>
);

const Notice = ({ tone = "amber", children }) => {
  const tones = {
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    red: "border-red-200 bg-red-50 text-red-700",
    gray: "border-gray-200 bg-gray-50 text-gray-600",
  };
  return (
    <div className={`flex items-start gap-1.5 rounded-md border px-2 py-1.5 text-[11px] leading-snug ${tones[tone]}`}>
      <WarningAmberOutlinedIcon sx={{ fontSize: 14, mt: "1px" }} />
      <span>{children}</span>
    </div>
  );
};

const TryItOutDialog = ({ api, open, onClose, apiToken, onAuthorize }) => {
  const token = apiToken;
  const hasBody = api ? BODY_METHODS.includes(api.method) : false;
  const tokenRequired = api ? needsToken(api) : false;

  const [url, setUrl] = useState("");
  const [body, setBody] = useState("");
  const [showBody, setShowBody] = useState(false);
  const [pathValues, setPathValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const pathParams = useMemo(() => (api ? pathParamsOf(api.endpoint) : []), [api]);
  const fields = useMemo(() => (api ? fieldsOf(api) : []), [api]);
  // Fields shown in the JSON editor. POST/PUT/PATCH: the request body (path
  // placeholders keep their own inputs). GET/DELETE: path placeholders + query
  // parameters - the JSON is only the input format; they are sent in the URL.
  const editorFields = useMemo(
    () => (hasBody ? fields : [...pathParams.map((name) => ({ name, rules: "required", from: "path" })), ...fields]),
    [hasBody, fields, pathParams],
  );

  const reset = () => {
    if (!api) return;
    setPathValues({});
    setUrl(buildUrl(api.endpoint));
    setBody(editorFields.length ? buildBody(editorFields) : "");
    setShowBody(editorFields.length > 0);
    setResponse(null);
  };

  // Fresh empty template each time the dialog opens for an endpoint.
  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, api]);

  // POST/PUT path inputs keep the URL in step.
  const setPath = (name, value) => {
    const next = { ...pathValues, [name]: value };
    setPathValues(next);
    setUrl(buildUrl(api.endpoint, next));
  };

  // GET/DELETE: the URL is rebuilt from the JSON values as they are typed.
  const changeBody = (text) => {
    setBody(text);
    if (hasBody) return;
    try {
      const obj = JSON.parse(text);
      if (obj && typeof obj === "object" && !Array.isArray(obj)) setUrl(buildUrl(api.endpoint, obj, true));
    } catch (e) {
      // keep the last valid URL until the JSON is valid again
    }
  };

  const fileParams = useMemo(() => (api?.params || []).filter((p) => isFileParam(p.rules)), [api]);
  const unresolved = url.match(/\{[^}]+\}/g) || [];

  const parsedBody = useMemo(() => {
    if (!showBody || !body.trim()) return { value: null, error: "" };
    try {
      const value = JSON.parse(body);
      if (!hasBody && (!value || typeof value !== "object" || Array.isArray(value))) {
        return { value: null, error: "Parameters must be a JSON object." };
      }
      return { value, error: "" };
    } catch (e) {
      return { value: null, error: hasBody ? "Request body is not valid JSON." : "Parameters are not valid JSON." };
    }
  }, [hasBody, showBody, body]);
  const bodyError = parsedBody.error;

  if (!api) return null;

  // Required values still empty (warning only, as for POST/PUT).
  const missingRequired =
    parsedBody.value && typeof parsedBody.value === "object"
      ? editorFields.filter((p) => isRequired(p.rules) && isEmptyValue(parsedBody.value[p.name])).map((p) => p.name)
      : [];
  // POST/PUT path placeholders still empty.
  const paramMissing = hasBody ? pathParams.filter((n) => !pathValues[n]) : [];
  // DELETE: never send without the user naming the record explicitly.
  const deleteNeedsValue =
    api.method === "DELETE" &&
    editorFields.length > 0 &&
    !(parsedBody.value && Object.values(parsedBody.value).some((v) => !isEmptyValue(v)));

  const headers = {
    Accept: "application/json",
    ...(hasBody && showBody ? { "Content-Type": "application/json" } : {}),
    ...(tokenRequired && token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const send = async () => {
    setLoading(true);
    setResponse(null);
    const started = performance.now();
    const sentBody = hasBody && showBody && body.trim() ? stripEmptyOptional(body, fields) : "";
    const request = {
      method: api.method,
      url,
      headers: Object.entries(headers).map(([k, v]) => [k, k === "Authorization" ? `Bearer ${maskToken(token)}` : v]),
      body: sentBody,
    };
    try {
      const res = await fetch(url, {
        method: api.method,
        headers,
        ...(sentBody ? { body: sentBody } : {}),
      });
      const text = await res.text();
      let pretty = text;
      let isJson = false;
      try {
        pretty = JSON.stringify(JSON.parse(text), null, 2);
        isJson = true;
      } catch (e) {
        // not JSON - show as text
      }
      const resHeaders = [];
      res.headers.forEach((value, key) => resHeaders.push([key, value]));
      setResponse({
        status: res.status,
        statusText: res.statusText,
        ms: Math.round(performance.now() - started),
        headers: resHeaders,
        body: pretty,
        isJson,
        request,
      });
    } catch (e) {
      setResponse({
        status: 0,
        statusText: "Network error",
        ms: Math.round(performance.now() - started),
        headers: [],
        body: `${e.message || e}\n\nThe request did not reach the server or was blocked by the browser (CORS / offline / invalid URL).`,
        isJson: false,
        request,
      });
    } finally {
      setLoading(false);
    }
  };

  const copyResponse = () => {
    if (!response) return;
    navigator.clipboard?.writeText(response.body).then(
      () => showSuccess("Copied response"),
      () => {},
    );
  };

  const canSend =
    !loading && !bodyError && unresolved.length === 0 && !deleteNeedsValue && url.trim() !== "";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: "12px" } }}>
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2.5">
        <span
          className={`inline-flex min-w-[50px] justify-center rounded border px-1.5 py-px text-[10px] font-semibold tracking-wide ${
            METHOD_STYLES[api.method] || "bg-gray-50 text-gray-700 border-gray-200"
          }`}
        >
          {api.method}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-gray-900">Try It Out</div>
          <code className="block truncate font-mono text-[11px] text-gray-500" title={api.endpoint}>
            {api.endpoint}
          </code>
        </div>
        <Tooltip title="Reset request" arrow>
          <button type="button" onClick={reset} className="rounded p-1 text-gray-500 hover:bg-gray-100" aria-label="Reset request">
            <RestartAltOutlinedIcon sx={{ fontSize: 18 }} />
          </button>
        </Tooltip>
        <Tooltip title="Close" arrow>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100" aria-label="Close">
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </Tooltip>
      </div>

      <div className="flex max-h-[75vh] flex-col gap-3 overflow-y-auto px-4 py-3">
        {api.handlerMissing && (
          <Notice tone="red">This route has no controller handler, so the request is expected to fail.</Notice>
        )}
        {api.method !== "GET" && (
          <Notice>This sends a real {api.method} request to {baseUrl} and may create, change or delete data.</Notice>
        )}

        {/* Same section order for every method: URL, Headers, Parameters, Body */}
        <Section
          title="Request URL"
          footer={
            !hasBody && editorFields.length > 0 ? (
              <Hint>Built from the parameters below — empty values are left out.</Hint>
            ) : (
              hasBody && pathParams.length > 0 && <Hint>Built from the path parameters below.</Hint>
            )
          }
        >
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            spellCheck={false}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs text-gray-800 focus:border-nbl-primary focus:outline-none"
          />
        </Section>

        <section>
          <Label>Headers</Label>
          <div className="divide-y divide-gray-100 rounded-md border border-gray-200 font-mono text-[11px]">
            <div className="flex gap-2 px-2 py-1">
              <span className="w-28 shrink-0 text-gray-500">Accept</span>
              <span className="text-gray-800">application/json</span>
            </div>
            {hasBody && (
              <div className="flex gap-2 px-2 py-1">
                <span className="w-28 shrink-0 text-gray-500">Content-Type</span>
                <span className="text-gray-800">application/json</span>
              </div>
            )}
            <div className="flex items-center gap-2 px-2 py-1">
              <span className="w-28 shrink-0 text-gray-500">Authorization</span>
              {!tokenRequired ? (
                <span className="text-gray-400">Not required (public)</span>
              ) : token ? (
                <span className="truncate text-gray-800">Bearer {maskToken(token)}</span>
              ) : (
                <span className="flex items-center gap-2 font-sans">
                  <span className="text-red-600">Not authorized</span>
                  <button type="button" onClick={onAuthorize} className="font-semibold text-nbl-primary hover:underline">
                    Authorize
                  </button>
                </span>
              )}
            </div>
          </div>
          <div className="mt-1 flex items-center gap-1 text-[11px] text-gray-500">
            <LockOutlinedIcon sx={{ fontSize: 12 }} />
            {tokenRequired
              ? token
                ? "Protected — the token from Authorize is sent automatically."
                : "Protected — click Authorize (top of the page) to set a Bearer token. Without it the API will reject the request."
              : `Auth: ${api.auth}. No token is sent.`}
          </div>
        </section>

        {/* POST/PUT/PATCH: route placeholders such as {id} (not part of the body) */}
        {hasBody && pathParams.length > 0 && (
          <Section
            title="Path parameters"
            footer={paramMissing.length > 0 && <Hint tone="amber">Required but empty: {paramMissing.join(", ")}</Hint>}
          >
            <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 rounded-md border border-gray-300 p-2.5 sm:grid-cols-2">
              {pathParams.map((name) => (
                <ParamField
                  key={`path-${name}`}
                  name={name}
                  required
                  where="path"
                  hint="Part of the URL"
                  value={pathValues[name] || ""}
                  onChange={(v) => setPath(name, v)}
                />
              ))}
            </div>
          </Section>
        )}
        {!hasBody && editorFields.length === 0 && (
          <Section title="Parameters">
            <div className="rounded-md border border-dashed border-gray-300 px-2.5 py-2 text-[11px] text-gray-500">
              This endpoint takes no parameters.
            </div>
          </Section>
        )}

        {/* Body */}
        {hasBody && !showBody && (
          <Section title="Request body (JSON)">
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-gray-300 px-2.5 py-2 text-[11px] text-gray-500">
              <span>This endpoint doesn't read any request body fields.</span>
              <button
                type="button"
                onClick={() => {
                  setBody("{\n  \n}");
                  setShowBody(true);
                }}
                className="font-semibold text-nbl-primary hover:underline"
              >
                Add a JSON body anyway
              </button>
            </div>
          </Section>
        )}
        {/* One JSON editor for every method. POST/PUT/PATCH send it as the body;
            GET/DELETE use it only as input and send the values in the URL. */}
        {showBody && (
          <div>
            <Label
              extra={
                bodyError ? (
                  <span className="text-[11px] normal-case text-red-600">{bodyError}</span>
                ) : (
                  !hasBody && <span className="text-[10px] normal-case text-gray-400">sent as URL query parameters</span>
                )
              }
            >
              {hasBody ? "Request body (JSON)" : "Request parameters (JSON)"}
            </Label>
            {editorFields.length > 0 && (
              <div className="mb-1.5 flex flex-wrap items-center gap-1">
                {editorFields.map((p) => (
                  <Tooltip key={p.name} title={ruleHint(p) || "no rules"} arrow>
                    <span
                      className={`inline-flex items-center gap-1 rounded border px-1.5 py-px font-mono text-[10px] ${
                        isRequired(p.rules)
                          ? missingRequired.includes(p.name)
                            ? "border-red-200 bg-red-50 text-red-700"
                            : "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-gray-200 bg-gray-50 text-gray-600"
                      }`}
                    >
                      {p.name}
                      {isRequired(p.rules) && <span className="font-sans font-bold">*</span>}
                    </span>
                  </Tooltip>
                ))}
              </div>
            )}
            <textarea
              value={body}
              onChange={(e) => changeBody(e.target.value)}
              spellCheck={false}
              rows={Math.min(14, Math.max(4, body.split("\n").length))}
              className={`w-full resize-y rounded-md border px-2 py-1.5 font-mono text-xs text-gray-800 focus:outline-none ${
                bodyError ? "border-red-300" : "border-gray-300 focus:border-nbl-primary"
              }`}
            />
            {editorFields.length > 0 &&
              (hasBody ? (
                <div className="mt-1 text-[11px] text-gray-500">
                  Fill in the values. <span className="font-semibold text-red-600">*</span> = required by the API's validation; empty
                  optional fields are left out when sending.
                </div>
              ) : (
                <div className="mt-1 text-[11px] text-gray-500">
                  Fill in the values. <span className="font-semibold text-red-600">*</span> = required; empty values are left out.
                  No request body is sent for {api.method} — the values go in the URL.
                </div>
              ))}
            {api.method === "DELETE" && (
              <div className="mt-1 text-[11px] text-red-700">
                Enter the value of the record you want to delete. Nothing is pre-selected — the request is only sent when you click
                Send.
              </div>
            )}
            {deleteNeedsValue && !bodyError && (
              <div className="mt-1 text-[11px] text-amber-700">Enter the value identifying the record before sending.</div>
            )}
            {missingRequired.length > 0 && !bodyError && (
              <div className="mt-1 text-[11px] text-amber-700">Required but empty: {missingRequired.join(", ")}</div>
            )}
            {fileParams.length > 0 && (
              <div className="mt-1.5">
                <Notice tone="gray">
                  File upload field{fileParams.length > 1 ? "s" : ""} {fileParams.map((p) => p.name).join(", ")} can't be sent as
                  JSON and {fileParams.length > 1 ? "are" : "is"} left out of this request.
                </Notice>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={send}
            disabled={!canSend}
            className="flex items-center gap-1.5 rounded-md bg-nbl-primary px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SendOutlinedIcon sx={{ fontSize: 15 }} />
            {loading ? "Sending…" : "Send"}
          </button>
        </div>

        {/* Response */}
        {response && (
          <div className="flex flex-col gap-2 border-t border-gray-200 pt-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Response</span>
              <span className={`rounded border px-1.5 py-px text-[11px] font-semibold ${statusClass(response.status)}`}>
                {response.status || "—"} {response.statusText}
              </span>
              <span className="text-[11px] text-gray-500">{response.ms} ms</span>
              <button
                type="button"
                onClick={copyResponse}
                className="ml-auto flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50"
              >
                <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} /> Copy Response
              </button>
            </div>
            <details className="rounded-md border border-gray-200 text-[11px]">
              <summary className="cursor-pointer px-2 py-1 text-gray-600">Request sent</summary>
              <div className="flex flex-col gap-1.5 border-t border-gray-100 px-2 py-1.5 font-mono">
                <div className="break-all text-gray-800">
                  <span className="font-semibold">{response.request.method}</span> {response.request.url}
                </div>
                <div className="divide-y divide-gray-100 rounded border border-gray-100">
                  {response.request.headers.map(([k, v]) => (
                    <div key={k} className="flex gap-2 px-2 py-0.5">
                      <span className="w-36 shrink-0 text-gray-500">{k}</span>
                      <span className="break-all text-gray-800">{v}</span>
                    </div>
                  ))}
                </div>
                {response.request.body && (
                  <pre className="max-h-[160px] overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 p-1.5 text-gray-800">
                    {response.request.body}
                  </pre>
                )}
              </div>
            </details>
            {response.headers.length > 0 && (
              <details className="rounded-md border border-gray-200 text-[11px]">
                <summary className="cursor-pointer px-2 py-1 text-gray-600">Response headers ({response.headers.length})</summary>
                <div className="divide-y divide-gray-100 border-t border-gray-100 font-mono">
                  {response.headers.map(([k, v]) => (
                    <div key={k} className="flex gap-2 px-2 py-0.5">
                      <span className="w-36 shrink-0 text-gray-500">{k}</span>
                      <span className="break-all text-gray-800">{v}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
            <pre className="max-h-[320px] overflow-auto whitespace-pre-wrap break-words rounded-md bg-[#0f2739] p-2.5 font-mono text-[11px] leading-relaxed text-emerald-100">
              {response.body || "(empty response)"}
            </pre>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default TryItOutDialog;
